import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, Trash2, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getUserDisplayName } from '../../../lib/user-utils';
import { useUser } from '../../../hooks/queries/useAuth';
import { MarkdownContent } from '../../shared/ContentStyles';
import { FormattedChatMessage } from '../../../hooks/queries/chatTypes';
import { useChatMessages } from '../../../hooks/queries/useChats';

interface ChatMessageDetailPanelProps {
  message: FormattedChatMessage;
  onClose: () => void;
  onDelete: (messageId: string) => void;
  visible: boolean;
}

export function ChatMessageDetailPanel({
  message,
  onClose,
  onDelete,
  visible,
}: ChatMessageDetailPanelProps) {
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { data: user } = useUser();
  const [userMessage, setUserMessage] = useState<FormattedChatMessage | null>(null);

  // Get the topic ID for the message to fetch related messages
  const topicId = message.topicId;

  // Use React Query hook to fetch messages
  const { data: messages = [] } = useChatMessages(topicId);

  // Format the date
  const date = new Date(message.createdAt || Date.now());

  // Find the corresponding user message if this is an assistant message
  useEffect(() => {
    if (message.role === 'assistant') {
      // Find the user message that came before this assistant message
      const index = messages.findIndex(m => m.id === message.id);
      if (index > 0 && messages[index - 1].role === 'user') {
        setUserMessage(messages[index - 1]);
      }
    } else {
      // This is a user message, find the assistant response if any
      const index = messages.findIndex(m => m.id === message.id);
      if (index < messages.length - 1 && messages[index + 1].role === 'assistant') {
        // We're showing a user message detail, so there's no need to set userMessage
        // as the current message is already the user message
        setUserMessage(null);
      }
    }
  }, [message.id, message.role, messages]);

  // Auto-expand short messages
  useEffect(() => {
    const content = userMessage ? userMessage.content : message.content;
    if (content.length <= 250) {
      setIsQuestionExpanded(true);
    } else {
      setIsQuestionExpanded(false);
    }
  }, [message.content, userMessage]);

  // Get user display name
  const isCurrentUser = user?.id === (userMessage?.userId || message.userId);
  const displayMessage = message.role === 'assistant' && userMessage ? userMessage : message;
  const userDisplayName = isCurrentUser
    ? getUserDisplayName(user)
    : (displayMessage.userMetadata?.displayName ?? 'User');

  return (
    <div
      className={cn(
        'fixed top-0 right-0 bottom-0 bg-transparent transition-all duration-500 ease-in-out flex flex-col z-50',
        visible ? 'transform-none delay-75' : 'translate-x-full',
        // On mobile, take full width
        'w-full left-0 lg:left-auto',
        // On desktop, match the width of the chat content area
        'lg:w-[calc(100%-560px)]'
      )}
      style={{ isolation: 'isolate' }}
    >
      {/* Single scrollable container that includes header and content */}
      <div className="flex-1 overflow-y-auto m-3 bg-white rounded-lg">
        {/* Header with back and action buttons - scrolls with content (no sticky positioning) */}
        <div className="p-4 flex items-center justify-between bg-gray-50 rounded-t-lg">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
            <span className="text-sm font-medium">Back to chat</span>
          </button>

          <button
            onClick={() => setShowDeleteConfirmation(true)}
            className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-all"
            title="Delete message"
          >
            <Trash2 size={18} />
          </button>
        </div>

        {/* Content area with padding */}
        <div className="py-4 px-10">
          <div className="max-w-3xl mx-auto">
            {/* Message header with title and metadata */}
            <div className="mb-6">
              <div className="flex flex-col">
                {/* Title area with message summary or default title */}
                <div className="w-full">
                  {message.summary ? (
                    <h2 className="text-2xl font-bold text-gray-800 leading-normal line-clamp-2">
                      {message.summary}
                    </h2>
                  ) : (
                    <h2 className="text-2xl font-bold text-gray-800 leading-normal line-clamp-2">
                      {message.role === 'assistant' ? 'AI Response' : 'Question'}
                    </h2>
                  )}

                  {/* Meta info: Date and sender on the same line */}
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <span>
                      {date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className="flex items-center">
                      <span className="mx-1">•</span> {userDisplayName}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Message content with clean styling */}
            <div className="space-y-6">
              {/* User question - if this is an assistant message or if we're viewing a user message */}
              {(userMessage || message.role === 'user') && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-blue-600">Question</p>
                    {(userMessage ? userMessage.content : message.content).length > 250 && (
                      <button
                        onClick={() => setIsQuestionExpanded(!isQuestionExpanded)}
                        className="text-gray-400 hover:text-blue-500 transition-colors text-xs font-medium"
                      >
                        {isQuestionExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                  <div>
                    <div className="text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {isQuestionExpanded
                        ? userMessage
                          ? userMessage.content
                          : message.content
                        : (userMessage ? userMessage.content : message.content).length > 250
                          ? (userMessage ? userMessage.content : message.content).substring(
                              0,
                              250
                            ) + '...'
                          : userMessage
                            ? userMessage.content
                            : message.content}
                    </div>
                  </div>
                </div>
              )}

              {/* AI answer with cleaner design */}
              {message.role === 'assistant' && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-green-600">Response</p>
                  </div>
                  <div className="bg-white rounded-lg">
                    <MarkdownContent content={message.content} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 overflow-hidden m-3">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center text-red-600">
                <AlertTriangle size={20} className="mr-2" />
                <h3 className="font-bold">Delete Confirmation</h3>
              </div>
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this message? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onDelete(message.id);
                    setShowDeleteConfirmation(false);
                  }}
                  className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
